import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';

// Environment configurations
const ENVIRONMENTS = {
  production: {
    name: 'Production',
    UserPoolId: 'us-east-1_fsETNijSa',
    ClientId: '5789bdis1s6hi1s1a4kr6obf5p',
    wsUrl: 'wss://ws.inquisitor.app'
  },
  development: {
    name: 'Development',
    UserPoolId: 'us-east-1_0nfCdl6DW',
    ClientId: '4obumea3933jfkhoqnh3i8jf8p',
    wsUrl: 'wss://tsmfgn0lua.execute-api.us-east-1.amazonaws.com/dev'
  }
};

const STORAGE_KEY = 'inquisitor_auth_env';

/**
 * Get the current environment key from localStorage
 */
export const getCurrentEnvironment = () => {
  return localStorage.getItem(STORAGE_KEY) || 'production';
};

/**
 * Set the current environment
 * @param {string} envKey - 'production' or 'development'
 */
export const setEnvironment = (envKey) => {
  if (!ENVIRONMENTS[envKey]) {
    throw new Error(`Unknown environment: ${envKey}`);
  }
  localStorage.setItem(STORAGE_KEY, envKey);
  // Reset the user pool so it gets re-initialized with new config
  userPool = null;
  // Also update the WebSocket URL so WebRTCContext uses the correct backend
  const wsUrl = ENVIRONMENTS[envKey].wsUrl;
  if (wsUrl) {
    localStorage.setItem('wsUrl', wsUrl);
  }
};

/**
 * Get the WebSocket URL for the current environment
 */
export const getEnvironmentWsUrl = () => {
  const envKey = getCurrentEnvironment();
  return ENVIRONMENTS[envKey]?.wsUrl || '';
};

/**
 * Get available environments for the dropdown
 */
export const getAvailableEnvironments = () => {
  return Object.entries(ENVIRONMENTS).map(([key, config]) => ({
    key,
    name: config.name
  }));
};

let userPool = null;

/**
 * Initialize the user pool (lazy initialization)
 */
const getUserPool = () => {
  const envKey = getCurrentEnvironment();
  const config = ENVIRONMENTS[envKey];

  if (!userPool && config.UserPoolId && config.ClientId) {
    userPool = new CognitoUserPool({
      UserPoolId: config.UserPoolId,
      ClientId: config.ClientId
    });
  }
  return userPool;
};

/**
 * Get the current authenticated user
 * @returns {CognitoUser|null}
 */
export const getCurrentUser = () => {
  const pool = getUserPool();
  return pool ? pool.getCurrentUser() : null;
};

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} User session data
 */
export const signIn = (email, password) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const userData = {
          id: session.getIdToken().payload.sub,
          email: session.getIdToken().payload.email,
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        };

        // Store user data for getCurrentUser helper
        localStorage.setItem('gogo_current_user', JSON.stringify({
          id: userData.id,
          email: userData.email,
          title: userData.email,
          role: 0 // Admin role
        }));

        resolve(userData);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Handle new password required challenge
        reject({
          code: 'NewPasswordRequired',
          message: 'New password required',
          userAttributes,
          requiredAttributes,
          cognitoUser
        });
      }
    });
  });
};

/**
 * Sign up a new user
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} Sign up result
 */
export const signUp = (email, password) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email
      })
    ];

    pool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({
        user: result.user,
        userConfirmed: result.userConfirmed,
        userSub: result.userSub
      });
    });
  });
};

/**
 * Confirm sign up with verification code
 * @param {string} email
 * @param {string} code
 * @returns {Promise<string>}
 */
export const confirmSignUp = (email, code) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool
    });

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Resend confirmation code
 * @param {string} email
 * @returns {Promise<Object>}
 */
export const resendConfirmationCode = (email) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool
    });

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Initiate forgot password flow
 * @param {string} email
 * @returns {Promise<Object>}
 */
export const forgotPassword = (email) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool
    });

    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        resolve(data);
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Confirm password reset with verification code
 * @param {string} email
 * @param {string} code
 * @param {string} newPassword
 * @returns {Promise<string>}
 */
export const confirmPassword = (email, code, newPassword) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve('Password confirmed successfully');
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOut = () => {
  return new Promise((resolve) => {
    const cognitoUser = getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    // Clear stored user data
    localStorage.removeItem('gogo_current_user');
    resolve();
  });
};

/**
 * Get the current session (refreshes if needed)
 * @returns {Promise<Object|null>}
 */
export const getSession = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve({
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        userId: session.getIdToken().payload.sub,
        email: session.getIdToken().payload.email
      });
    });
  });
};

/**
 * Get the current access token (for WebSocket authentication)
 * @returns {Promise<string|null>}
 */
export const getAccessToken = async () => {
  const session = await getSession();
  return session ? session.accessToken : null;
};

/**
 * Check if there is a valid authenticated session
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  try {
    const session = await getSession();
    return session !== null;
  } catch {
    return false;
  }
};

/**
 * Change password for authenticated user
 * @param {string} oldPassword
 * @param {string} newPassword
 * @returns {Promise<string>}
 */
export const changePassword = (oldPassword, newPassword) => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        reject(new Error('Invalid session'));
        return;
      }

      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  });
};

/**
 * Complete new password challenge
 * @param {CognitoUser} cognitoUser - The user from the NewPasswordRequired callback
 * @param {string} newPassword
 * @param {Object} requiredAttributes
 * @returns {Promise<Object>}
 */
export const completeNewPasswordChallenge = (cognitoUser, newPassword, requiredAttributes = {}) => {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
      onSuccess: (session) => {
        const userData = {
          id: session.getIdToken().payload.sub,
          email: session.getIdToken().payload.email,
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        };

        localStorage.setItem('gogo_current_user', JSON.stringify({
          id: userData.id,
          email: userData.email,
          title: userData.email,
          role: 0
        }));

        resolve(userData);
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

export default {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resendConfirmationCode,
  forgotPassword,
  confirmPassword,
  getSession,
  getAccessToken,
  isAuthenticated,
  getCurrentUser,
  changePassword,
  completeNewPasswordChallenge
};
